import { LitElement, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhCopy";
import "@phosphor-icons/webcomponents/PhLink";
import "@phosphor-icons/webcomponents/PhDownloadSimple";
import "@phosphor-icons/webcomponents/PhXLogo";
import "@phosphor-icons/webcomponents/PhFacebookLogo";
import "@phosphor-icons/webcomponents/PhInstagramLogo";
import "@phosphor-icons/webcomponents/PhTiktokLogo";
import "ui-components/modal/wow-modal";
import "ui-components/image-or-video/image-or-video";
import { NotificationService } from "ui-components/notification";

import { styles } from "./share-content-modal.css";
import { t, translate } from "../../translations/i18n";
import { getAssetUrl } from "helpers/assetHelpers";

export interface ShareContentDetails {
  id: string;
  title: string;
  thumbnail_url?: string | null;
  shareUrl: string;
}

export type ThumbnailOrientation = "horizontal" | "vertical";

@customElement("share-content-modal")
export class ShareContentModal extends LitElement {
  static override styles = [styles];

  @property({ type: Object })
  content!: ShareContentDetails;

  @property({ type: String, attribute: "thumbnail-orientation" })
  thumbnailOrientation: ThumbnailOrientation = "horizontal";

  @state()
  private isCopied = false;

  private get thumbnailUrl(): string | undefined {
    if (!this.content.thumbnail_url) return undefined;
    return getAssetUrl(this.content.thumbnail_url);
  }

  private async handleCopyLink() {
    try {
      await navigator.clipboard.writeText(this.content.shareUrl);
      this.isCopied = true;
      NotificationService.success(translate("shareContentModal.linkCopied"));

      setTimeout(() => {
        this.isCopied = false;
      }, 2000);
    } catch {
      NotificationService.error(translate("shareContentModal.copyFailed"));
    }
  }

  private async handleDownloadImage() {
    if (!this.thumbnailUrl) return;

    try {
      const response = await fetch(this.thumbnailUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${this.content.title.replace(/[^a-z0-9]/gi, "_")}_promo.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      NotificationService.success(translate("shareContentModal.imageDownloaded"));
    } catch {
      NotificationService.error(translate("shareContentModal.downloadFailed"));
    }
  }

  private handleShareX() {
    const text = translate("shareContentModal.shareText", { title: this.content.title });
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(this.content.shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  private handleShareFacebook() {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(this.content.shareUrl)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  private handleDownloadForInstagram() {
    this.handleDownloadImage();
  }

  private handleDownloadForTikTok() {
    this.handleDownloadImage();
  }

  private handleClose() {
    this.dispatchEvent(new CustomEvent("modal-close", { bubbles: true }));
  }

  protected override render() {
    return html`
      <wow-modal mobile-fullscreen hide-primary-button @close=${this.handleClose}>
        <span slot="header">${t("shareContentModal.header")}</span>

        <div class="share-content">
          <div class="promo-section">
            <h2 class="section-title">${t("shareContentModal.promoCardTitle")}</h2>
            <p class="section-subtitle">${t("shareContentModal.promoCardSubtitle")}</p>

            <div class="promo-content">
              <div class="promo-preview ${this.thumbnailOrientation}">
                ${this.thumbnailUrl
                  ? html`<image-or-video url=${this.thumbnailUrl} alt=${this.content.title}></image-or-video>`
                  : html`<div class="promo-placeholder">
                      <span>${this.content.title}</span>
                    </div>`}
              </div>

              <div class="share-actions">
                ${this.thumbnailUrl
                  ? html`
                      <button class="share-action-btn" @click=${this.handleDownloadImage}>
                        <ph-download-simple size="20"></ph-download-simple>
                        ${t("shareContentModal.downloadImage")}
                      </button>
                    `
                  : nothing}

                <button class="share-action-btn" @click=${this.handleShareX}>
                  <ph-x-logo size="20"></ph-x-logo>
                  ${t("shareContentModal.shareOnX")}
                </button>

                <button class="share-action-btn" @click=${this.handleShareFacebook}>
                  <ph-facebook-logo size="20"></ph-facebook-logo>
                  ${t("shareContentModal.shareOnFacebook")}
                </button>

                ${this.thumbnailUrl
                  ? html`
                      <button class="share-action-btn" @click=${this.handleDownloadForInstagram}>
                        <ph-instagram-logo size="20"></ph-instagram-logo>
                        ${t("shareContentModal.downloadForInstagram")}
                      </button>

                      <button class="share-action-btn" @click=${this.handleDownloadForTikTok}>
                        <ph-tiktok-logo size="20"></ph-tiktok-logo>
                        ${t("shareContentModal.downloadForTikTok")}
                      </button>
                    `
                  : nothing}
              </div>
            </div>
          </div>

          <div class="link-section">
            <h3 class="link-title">${t("shareContentModal.linkTitle")}</h3>
            <p class="link-subtitle">${t("shareContentModal.linkSubtitle")}</p>

            <div class="link-row">
              <div class="link-input-container">
                <ph-link size="20"></ph-link>
                <input type="text" class="link-input" .value=${this.content.shareUrl} readonly />
              </div>
              <button class="copy-link-btn ${this.isCopied ? "copied" : ""}" @click=${this.handleCopyLink}>
                <ph-copy size="20"></ph-copy>
                ${t("shareContentModal.copyLink")}
              </button>
            </div>
          </div>
        </div>
      </wow-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "share-content-modal": ShareContentModal;
  }
}
